import { classNames } from 'libs/react';
import { useRef } from 'libs/react';
import { useState } from 'libs/react';
import { MediaKind } from 'libs/media/MediaType';
import { MediaType } from 'libs/media/MediaType';
import { React } from 'libs/react';
import { Icon } from 'components/Icon';
import { Text } from 'components/Text';
import './MediaViewer.scss';
/**
 * @component MediaViewer
 * @since 1.0.0
 */
export function MediaViewer(props) {
    const { style, media } = Object.assign({ style: 'normal' }, props);
    if (media == null) {
        return null;
    }
    let type = '';
    let view = null;
    switch (MediaType.getKind(media.mimetype)) {
        case MediaKind.Image:
            view = React.createElement(Image, { media: media });
            type = 'image';
            break;
        case MediaKind.Video:
            view = React.createElement(Video, { media: media });
            type = 'video';
            break;
        case MediaKind.Audio:
            view = React.createElement(Audio, { media: media });
            type = 'audio';
            break;
        case MediaKind.Pdf:
            view = React.createElement(Pdf, { media: media });
            type = 'application/pdf';
            break;
    }
    const className = classNames('media-viewer', 'media-viewer--' + type, 'media-viewer--' + style, props.className);
    return React.createElement("div", { className: className }, view);
}
/**
 * @component Image
 * @since 1.0.0
 * @hidden
 */
function Image(props) {
    const { media } = props;
    return React.createElement("img", { loading: "lazy", src: media.thumbnail || '' });
}
/**
 * @component Pdf
 * @since 1.0.0
 * @hidden
 */
function Pdf(props) {
    const { media } = props;
    return (React.createElement("div", null,
        React.createElement(Icon, { value: "file-pdf", className: "media-viewer-pdf-icon" }),
        React.createElement(Text, { value: media.filename, className: "media-viewer-pdf-text" })));
}
/**
 * @component Video
 * @since 1.0.0
 * @hidden
 */
function Video(props) {
    const { media } = props;
    const node = useRef(null);
    const video = useRef(null);
    const [playing, setPlaying] = useState(false);
    /**
     * @function getProgress
     * @since 1.0.0
     * @hidden
     */
    function getProgress() {
        if (video.current == null) {
            return 0;
        }
        const played = video.current.currentTime;
        const length = video.current.duration;
        return (played / length) * 100;
    }
    /**
     * @function onPlay
     * @since 1.0.0
     * @hidden
     */
    function onPlay(e) {
        e.stopPropagation();
        if (video.current) {
            video.current.play();
        }
        setPlaying(true);
    }
    /**
     * @function onStop
     * @since 1.0.0
     * @hidden
     */
    function onStop(e) {
        e.stopPropagation();
        if (video.current) {
            video.current.pause();
            video.current.currentTime = 0;
        }
        setPlaying(false);
    }
    /**
     * @function onPause
     * @since 1.0.0
     * @hidden
     */
    function onPause() {
        setPlaying(false);
    }
    /**
     * @function onEnded
     * @since 1.0.0
     * @hidden
     */
    function onEnded() {
        setPlaying(false);
    }
    /**
     * @function onProgress
     * @since 1.0.0
     * @hidden
     */
    function onProgress(e) {
        if (node.current) {
            node.current.style.setProperty('--progress', getProgress() + '%');
        }
    }
    const className = classNames('media-viewer-player', 'media-viewer-player--video', { 'media-viewer-player--playing': playing });
    return (React.createElement("div", { ref: node, className: className },
        React.createElement("div", { className: "media-viewer-player-poster" },
            React.createElement("img", { loading: "lazy", src: media.thumbnail || '' })),
        React.createElement("div", { className: "media-viewer-player-object" },
            React.createElement(Play, { onClick: onPlay }),
            React.createElement(Stop, { onClick: onStop }),
            React.createElement("video", { ref: video, onPause: onPause, onEnded: onEnded, onTimeUpdate: onProgress },
                React.createElement("source", { type: media.mimetype, src: media.url })))));
}
/**
 * @component Audio
 * @since 1.0.0
 * @hidden
 */
function Audio(props) {
    const { media } = props;
    const node = useRef(null);
    const audio = useRef(null);
    const [playing, setPlaying] = useState(false);
    /**
     * @function getProgress
     * @since 1.0.0
     * @hidden
     */
    function getProgress() {
        if (audio.current == null) {
            return 0;
        }
        const played = audio.current.currentTime;
        const length = audio.current.duration;
        return (played / length) * 100;
    }
    /**
     * @function onPlay
     * @since 1.0.0
     * @hidden
     */
    function onPlay(e) {
        e.stopPropagation();
        if (audio.current) {
            audio.current.play();
        }
        setPlaying(true);
    }
    /**
     * @function onStop
     * @since 1.0.0
     * @hidden
     */
    function onStop(e) {
        e.stopPropagation();
        if (audio.current) {
            audio.current.pause();
            audio.current.currentTime = 0;
        }
        setPlaying(false);
    }
    /**
     * @function onPause
     * @since 1.0.0
     * @hidden
     */
    function onPause() {
        setPlaying(false);
    }
    /**
     * @function onEnded
     * @since 1.0.0
     * @hidden
     */
    function onEnded() {
        setPlaying(false);
    }
    /**
     * @function onProgress
     * @since 1.0.0
     * @hidden
     */
    function onProgress(e) {
        if (node.current) {
            node.current.style.setProperty('--progress', getProgress() + '%');
        }
    }
    const className = classNames('media-viewer-player', 'media-viewer-player--audio', { 'media-viewer-player--playing': playing });
    return (React.createElement("div", { ref: node, className: className },
        React.createElement("div", { className: "media-viewer-player-poster" },
            React.createElement("img", { loading: "lazy", src: media.thumbnail || '' })),
        React.createElement("div", { className: "media-viewer-player-object" },
            React.createElement(Play, { onClick: onPlay }),
            React.createElement(Stop, { onClick: onStop }),
            React.createElement("audio", { ref: audio, onPause: onPause, onEnded: onEnded, onTimeUpdate: onProgress },
                React.createElement("source", { type: media.mimetype, src: media.url })))));
}
/**
 * @component Play
 * @since 1.0.0
 * @hidden
 */
function Play(props) {
    return (React.createElement("svg", { className: "media-viewer-play", viewBox: "0 0 100 100", onClick: props.onClick },
        React.createElement("circle", { cx: "50", cy: "50", r: "50" }),
        React.createElement("path", { d: "M75.8 50L37.5 27.9v44.2z" })));
}
/**
 * @component Stop
 * @since 1.0.0
 * @hidden
 */
function Stop(props) {
    return (React.createElement("svg", { className: "media-viewer-stop", viewBox: "0 0 100 100", onClick: props.onClick },
        React.createElement("circle", { cx: "50", cy: "50", r: "50" }),
        React.createElement("rect", { x: "35", y: "35", width: "30", height: "30" })));
}
